import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { LoggerCodes, LoggerService } from '@shared/modules/logger/logger';
import { ET_DATE_FORMATTER } from '@sharedUtils';

import { resolveTermsContentVariant } from '../../terms.helpers';

export type BulletLinkSegment = { text: string; url: string };
export type BulletSegment = string | BulletLinkSegment;
export type BulletItem = string | { segments: BulletSegment[] };

export const BackupWithholdingContentVariant = {
  BrokerageClient: 'contentBrokerageClient',
  BrokerageProspect: 'contentBrokerageProspect',
  RetirementClient: 'contentRetirementClient',
  RetirementProspect: 'contentRetirementProspect',
} as const;
export type BackupWithholdingContentVariant =
  (typeof BackupWithholdingContentVariant)[keyof typeof BackupWithholdingContentVariant];

export enum TaxClassification {
  Individual = 'Individual',
  IRA = 'IRA',
  ESTATE = 'Estate',
  TRUST = 'Trust',
  W8 = 'W8',
}

export function resolveBackupWithholdingVariant(
  isIraAccount: boolean,
  isProspect: boolean,
): BackupWithholdingContentVariant {
  return resolveTermsContentVariant(isIraAccount, isProspect);
}

export function resolveTaxClassification(isIraAccount: boolean): TaxClassification {
  return TaxClassification.Individual;
}

export type BackupWithholdingVariantContent = {
  continueButtonLabel?: string;
  infoBox?: { boldText: string; text: string };
  w8Box?: { boldText: string; text: string };
  withholdingQuestion?: string;
  withholdingQuestionTooltip?: string;
  withholdingOptions?: {
    no: { label: string; badge: string };
    yes: {
      label: string;
      errorText: string;
      bannerTitle: string;
      bannerBodyStart: string;
      bannerLinkText: string;
      bannerLinkUrl: string;
      bannerBodyEnd: string;
    };
  };
  callout?: string;
  bullets?: BulletItem[];
  eSignature?: {
    title: string;
    tooltip: string;
    fullNameLabel: string;
    fullNameHint?: string;
    signatureHintEstate?: string;
    signatureHintTrust?: string;
    signatureHintGrantor?: string;
    signatureHintDefault?: string;
    signatureRequiredError?: string;
    ssnLabel: string;
    ssnRequiredError?: string;
    taxClassificationLabel: string;
    estateNameLabel?: string;
    estateTinLabel?: string;
    trustNameLabel?: string;
    trustDateLabel?: string;
    einLabel?: string;
    grantorNameLabel?: string;
    grantorSsnLabel?: string;
    federalTaxClassificationLabel?: string;
  };
};

@Component({
  selector: 'app-backup-withholding',
  templateUrl: './backup-withholding.component.html',
  styleUrls: ['./backup-withholding.component.scss'],
})
export class BackupWithholdingComponent implements OnChanges {
  @Input() content!: BackupWithholdingVariantContent;
  @Input() clientName: string = '';
  @Input() ssn: string = '';
  @Input() taxClassificationValue!: TaxClassification;
  @Input() isAccepted: boolean = false;
  @Input() acceptedAt: string | null = null;
  @Input() acceptAndContinueLabel: string = 'Accept and continue';
  @Input() blockOnYes: boolean = true;
  @Input() accountTypeLabel: string = '';

  @Output() acceptAndContinue = new EventEmitter<string>();
  @Output() revokeAcceptance = new EventEmitter<void>();

  acceptedDate: string = '';

  form = new FormGroup({
    withholding: new FormControl('no', { nonNullable: true }),
  });

  constructor(private readonly loggerService: LoggerService) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['acceptedAt']) {
      this.acceptedDate = this.acceptedAt ?? '';
    }
  }

  get withholdingIsYes(): boolean {
    return this.form.get('withholding')?.value === 'yes';
  }

  get isBlockedByYes(): boolean {
    return this.blockOnYes && this.withholdingIsYes;
  }

  get withholdingQuestionText(): string {
    return this.content?.withholdingQuestion ?? '';
  }

  get showAcceptedLabel(): boolean {
    return (this.isAccepted || !!this.acceptedDate) && !this.isBlockedByYes;
  }

  onWithholdingChange(): void {
    this.acceptedDate = '';

    if (this.isBlockedByYes && this.isAccepted) {
      this.revokeAcceptance.emit();
    }
  }

  isString(bullet: BulletItem): bullet is string {
    return typeof bullet === 'string';
  }

  isLink(segment: BulletSegment): segment is BulletLinkSegment {
    return typeof segment === 'object';
  }

  onAcceptAndContinue(): void {
    if (this.isBlockedByYes) {
      this.loggerService.info(
        {
          eventId: 'backup-withholding-blocked-yes',
          message: 'BackupWithholding.Accept.BlockedByYes',
        },
        LoggerCodes.NULL_FAILURE,
      );

      return;
    }

    this.acceptedDate = ET_DATE_FORMATTER.format(new Date());
    this.loggerService.info(
      { eventId: 'backup-withholding-accepted', message: 'BackupWithholding.Accept.Success' },
      LoggerCodes.GENERAL_SUCCESS,
    );
    this.acceptAndContinue.emit(this.acceptedDate);
  }
}
